/**
 * <AccountAvatarMenu> — ce qu'un compte peut faire de son avatar (template).
 *
 * Le menu de l'avatar de « Mon compte » (`CustomizableAvatar` de l'ancien code), et les deux boîtes
 * qui en partent : la confirmation de « Réinitialiser », et l'avis du web, où l'éditeur d'avatar
 * n'existe pas. Une entrée n'existe que si son geste est donné : l'élève a « Personnaliser mon
 * avatar », « Générer un avatar IA » et « Réinitialiser » ; le masteur, « Choisir une image » (une
 * photo, recadrée par l'écran) et « Réinitialiser ».
 *
 * Contrôlé : `step` dit ce qui est ouvert, chaque geste remonte, et c'est l'écran qui décide de
 * la suite (ouvrir l'éditeur ou l'avis, ouvrir la génération). L'éditeur reste un écran de l'ancien
 * code : on n'en porte que l'entrée ; la génération par IA est sa feuille, `<AccountAvatarAIModal>`.
 */
import React from "react"

import { ActionSheet } from "../../molecules/ActionSheet"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"

export type AccountAvatarMenuStep = "closed" | "menu" | "confirm-reset" | "mobile-only"

export type AccountAvatarMenuProps = {
    step: AccountAvatarMenuStep
    /** Ferme ce qui est ouvert, sans rien faire. */
    onClose: () => void
    /**
     * « Personnaliser mon avatar » : l'éditeur sur mobile, l'avis `mobile-only` sur le web. Absent :
     * pas d'entrée (le masteur n'a pas l'éditeur).
     */
    onCustomize?: () => void
    /**
     * « Générer un avatar IA ». Absent : pas d'entrée — un écran qui ne sait pas ouvrir la
     * génération (la feuille `<AccountAvatarAIModal>`, l'élève seul) ne la promet pas.
     */
    onGenerateAI?: () => void
    /** « Choisir une image » : la photo du masteur. Absent (défaut) : pas d'entrée, comme chez l'élève. */
    onPickImage?: () => void
    /** « Réinitialiser » : ouvre la confirmation. */
    onAskReset: () => void
    onConfirmReset: () => void
    testID?: string
}

export function AccountAvatarMenu(props: AccountAvatarMenuProps) {
    const { step, onClose, onCustomize, onGenerateAI, onPickImage, onAskReset, onConfirmReset, testID } = props

    return (
        <>
            <ActionSheet
                testID={testID}
                visible={step === "menu"}
                onClose={onClose}
                // Chaque ligne ouvre une suite que l'écran tient lui-même : refermer d'office
                // fermerait la confirmation avec le menu.
                closeOnSelect={false}
                items={[
                    ...(onCustomize != null
                        ? [{ icon: "user" as const, label: "Personnaliser mon avatar", onPress: onCustomize }]
                        : []),
                    ...(onGenerateAI != null
                        ? [{ icon: "magic" as const, label: "Générer un avatar IA", onPress: onGenerateAI }]
                        : []),
                    ...(onPickImage != null
                        ? [{ icon: "file-image" as const, label: "Choisir une image", onPress: onPickImage }]
                        : []),
                    { icon: "undo-alt", label: "Réinitialiser", onPress: onAskReset },
                ]}
            />

            <ConfirmDialog
                visible={step === "confirm-reset"}
                title="Veux-tu réinitialiser ton avatar ?"
                message="L'avatar actuel sera perdu"
                confirmLabel="Réinitialiser"
                onConfirm={onConfirmReset}
                onCancel={onClose}
            />

            <ConfirmDialog
                visible={step === "mobile-only"}
                title="Pour personnaliser ton avatar, il faut te connecter sur l'application mobile"
                confirmLabel="Quitter"
                danger={false}
                hideCancel
                onConfirm={onClose}
                onCancel={onClose}
            />
        </>
    )
}
